import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../../global/config/useSnackbar';
import { ADMIN_CONSOLE_ROLE_QUERY } from '../../../../../admin/admin-console/admin-console-content-pages/admin-console-roles/AdminConsoleRolesQuery/AdminConsoleRolesQuery.gql';
import { GetAdminConsoleRoles } from '../../../../../admin/admin-console/admin-console-content-pages/admin-console-roles/AdminConsoleRolesQuery/__generated__/GetAdminConsoleRoles';
import { RoleIcon } from '../../../../../../common/icons/RoleIconList';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  existingRoleIds: string[];
  onSuccess: () => void;
}

export const AddRoleApplicationDialog: React.FC<Props> = ({ open, onClose, userId, existingRoleIds, onSuccess }) => {
  const { theme } = useCampfireTheme();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const applyFetch = useCampfireFetch({ defer: true });

  const { data, loading } = useCampfireQuery<GetAdminConsoleRoles, undefined>(ADMIN_CONSOLE_ROLE_QUERY, {
    options: { skip: !open },
  });

  const availableRoles = useMemo(() => {
    return (data?.vm.activityRoles ?? []).filter((r) => !existingRoleIds.includes(r.activityRoleId));
  }, [data, existingRoleIds]);

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) => (prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]));
  };

  const handleClose = () => {
    setSelectedRoleIds([]);
    onClose();
  };

  const handleSubmit = () => {
    setSubmitting(true);
    Promise.all(
      selectedRoleIds.map((roleId) =>
        applyFetch.run({
          url: `${apiUrl}/vm/activity-roles/apply`,
          method: 'post',
          data: { activityRoleId: roleId, userId },
        })
      )
    ).then((results) => {
      const failed = results.find((res: any) => !res?.ok);
      if (failed) {
        setSnackbar({
          variant: 'error',
          message: (failed as any)?.data?.message ?? 'Failed to add role application',
          open: true,
        });
      } else {
        setSnackbar({ variant: 'success', message: 'Role applications added', open: true });
        setSelectedRoleIds([]);
        onSuccess();
        onClose();
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add Role Application</DialogTitle>
      <DialogContent style={{ padding: 0 }}>
        {loading ? (
          <Box display='flex' justifyContent='center' padding={4}>
            <CircularProgress size={24} />
          </Box>
        ) : availableRoles.length === 0 ? (
          <Box padding={3}>
            <Typography color='textSecondary'>No additional roles available.</Typography>
          </Box>
        ) : (
          <List style={{ paddingBottom: 0 }}>
            {availableRoles.map((role) => (
              <ListItem
                key={role.activityRoleId}
                button
                onClick={() => toggleRole(role.activityRoleId)}
                style={{ borderBottom: `1px solid ${theme.color.grey.border}`, padding: '12px 24px' }}
              >
                <ListItemIcon style={{ minWidth: 40 }}>
                  <Checkbox
                    checked={selectedRoleIds.includes(role.activityRoleId)}
                    edge='start'
                    disableRipple
                    color='primary'
                  />
                </ListItemIcon>
                {role.icon && (
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <RoleIcon iconName={role.icon} color={theme.color.grey.neutral300} style={{ height: 18 }} />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={
                    <Typography style={{ fontWeight: 600, fontSize: 15, color: '#444' }}>{role.name}</Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Box padding={1} display='flex' justifyContent='flex-end' width='100%'>
          <TabletButton variant='text' onClick={handleClose} disabled={submitting} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={selectedRoleIds.length === 0 || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Add'}
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
